import React from "react";
import { useSkin } from "../../skins/SkinContext";
import { SkRect, SkCircle, SkEllipse, SkLine } from "../../prim";
import { skSun, skCloud, skHouse } from "../../actors3";
import { skRiver, skStar, skMoon } from "../../actors3/nature";
import type { SkinSceneProps } from "./SkinParkScene";

/**
 * Anivaze v3 — two skin-aware NATURE concept scenes.
 *
 * SkinWaterCycleScene — teaches the water cycle: sun warms sea, droplets rise
 *   to form a cloud, rain falls on land, river flows back to sea (~8s loop).
 *
 * SkinDayNightScene — teaches day/night: Earth spins centre-stage, sun on the
 *   day side, moon+stars on the night side, house window lit/dark (~8s loop).
 */

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Helper — lerp a number                                                     */
/* ─────────────────────────────────────────────────────────────────────────── */
function lerp(a: number, b: number, p: number) {
  return a + (b - a) * Math.max(0, Math.min(1, p));
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  SCENE 1: Water Cycle                                                       */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * Water cycle loop (~8 s):
 *   0–2 s  sun heats sea → 5 droplets rise
 *   2–4 s  droplets join into a cloud that drifts right over land
 *   4–6 s  cloud rains onto green land
 *   6–8 s  river flows back to the sea; then loop
 */
export const SkinWaterCycleScene: React.FC<SkinSceneProps> = ({
  opacity = 1,
  t = 0,
  label = "Water goes round and round!",
}) => {
  const skin = useSkin();

  const CYCLE = 8;
  const phase = t % CYCLE; // 0–8 s

  /* ── static layout ── */
  const seaY = 330;   // top edge of sea band
  const groundY = 310; // top edge of green land (slightly above sea on land side)
  const landX = 420;  // x boundary between sea (left) and land (right)

  /* ── phase fractions ── */
  const riseP = Math.max(0, Math.min(1, (phase - 0) / 2));   // 0→1 during 0–2 s
  const driftP = Math.max(0, Math.min(1, (phase - 2) / 2));  // 0→1 during 2–4 s
  const rainP = Math.max(0, Math.min(1, (phase - 4) / 2));   // 0→1 during 4–6 s
  const flowP = Math.max(0, Math.min(1, (phase - 6) / 2));   // 0→1 during 6–8 s

  /* ── droplets: 5 droplets rise from sea surface → cloud position ── */
  const dropletSeeds = [0, 1, 2, 3, 4];
  const cloudTargetX = lerp(160, 520, driftP);
  const cloudTargetY = 130;

  /* ── rain drops falling from cloud ── */
  const rainDrops = [0, 1, 2, 3, 4, 5];

  /* ── river shimmer ── */
  const riverT = phase * 2; // speed up the river shimmer slightly

  /* ── sky cross-fade: day (light blue) during 0–6 s, dusk during 6–8 s ── */
  // We achieve this with a dark overlay fading in and out — but for simplicity
  // we leave the sky static (light blue bg) here. The sun / heat rays convey it.

  return (
    <g opacity={opacity}>
      {/* Background sky */}
      <rect width={800} height={450} fill={skin.surface} />

      {/* Sea band (left side) */}
      <SkRect x={0} y={seaY} w={landX} h={450 - seaY} fill="#38bdf8" seed={2001} />
      {/* Shimmering sea highlight */}
      <g transform={`translate(${Math.sin(t * 1.2) * 8} 0)`}>
        <SkEllipse cx={180} cy={seaY + 12} rx={120} ry={8} fill="#7dd3fc" seed={2002} />
        <SkEllipse cx={80} cy={seaY + 22} rx={60} ry={5} fill="#bae6fd" seed={2003} />
      </g>

      {/* Green land (right side) */}
      <SkRect x={landX} y={groundY} w={800 - landX} h={450 - groundY} fill="#86efac" seed={2010} />
      {/* Land / sea divider — a gentle shore */}
      <SkEllipse cx={landX} cy={seaY + 10} rx={30} ry={22} fill="#fde68a" seed={2011} />

      {/* Sun — top-left, heats the sea */}
      {skSun(100, 80, t, 1.2)}

      {/* Heat rays from sun toward sea: grow during riseP */}
      {[0, 1, 2].map((i) => {
        const rayOpacity = riseP * 0.5;
        const angle = 130 + i * 18; // degrees pointing down-right toward sea
        const rad = (angle * Math.PI) / 180;
        const len = 60 + i * 15;
        return (
          <SkLine
            key={i}
            x1={100 + Math.cos(rad) * 36}
            y1={80 + Math.sin(rad) * 36}
            x2={100 + Math.cos(rad) * (36 + len)}
            y2={80 + Math.sin(rad) * (36 + len)}
            stroke="#fbbf24"
            strokeWidth={2}
            opacity={rayOpacity}
            seed={2020 + i}
          />
        );
      })}

      {/* Rising droplets — rise from sea surface up to cloud height */}
      {dropletSeeds.map((i) => {
        const startX = 60 + i * 55;
        const startY = seaY - 5;
        // each droplet is slightly offset in timing so they don't all move together
        const localRise = Math.max(0, Math.min(1, riseP * 1.4 - i * 0.08));
        // after cloud forms, droplets disappear (they became the cloud)
        const dropOpacity = driftP > 0.5 ? Math.max(0, 1 - (driftP - 0.5) * 2) : 1;
        const dropY = lerp(startY, cloudTargetY + 10, localRise);
        const dropX = lerp(startX, cloudTargetX + (i - 2) * 16, localRise);
        if (localRise <= 0) return null;
        return (
          <g key={i} opacity={localRise * dropOpacity}>
            <SkCircle cx={dropX} cy={dropY} r={5 + i % 2} fill="#38bdf8" seed={2030 + i} />
          </g>
        );
      })}

      {/* Cloud — appears over sea, drifts to over land */}
      {driftP > 0 && (
        <g opacity={Math.min(1, driftP * 3)}>
          {skCloud(cloudTargetX, cloudTargetY, 1.4)}
        </g>
      )}

      {/* Rain drops falling from cloud onto land */}
      {rainDrops.map((i) => {
        const rainX = cloudTargetX - 30 + i * 14;
        const rainFallOffset = (rainP * 120 + i * 22) % 80; // stagger drops
        const rainOpacity = rainP > 0.1 ? 0.9 : 0;
        // only rain if cloud is over land (driftP > 0.6)
        if (driftP < 0.6) return null;
        return (
          <g key={i} opacity={rainOpacity}>
            <SkLine
              x1={rainX}
              y1={cloudTargetY + 25 + rainFallOffset}
              x2={rainX - 3}
              y2={cloudTargetY + 35 + rainFallOffset}
              stroke="#38bdf8"
              strokeWidth={2}
              seed={2040 + i}
            />
          </g>
        );
      })}

      {/* Puddle growing on land after rain */}
      {rainP > 0.3 && (
        <g opacity={Math.min(1, (rainP - 0.3) * 3)}>
          <SkEllipse cx={560} cy={groundY - 4} rx={lerp(0, 40, (rainP - 0.3) * 2)} ry={5} fill="#7dd3fc" seed={2050} />
        </g>
      )}

      {/* River flowing from land back to sea — flows in as flowP increases */}
      {flowP > 0 && (
        <g opacity={Math.min(1, flowP * 4)}>
          {skRiver(landX - 20, seaY - 14, riverT, lerp(0.1, 1.1, flowP))}
        </g>
      )}

      {/* Caption */}
      <g transform="translate(400 40)">
        <rect x={-235} y={-22} width={470} height={40} rx={20} fill="rgba(255,255,255,0.86)" stroke="#cbd5e1" strokeWidth={1.5} />
        <text x={0} y={4} textAnchor="middle" fill={skin.textInk} fontSize={16} fontWeight={700} fontFamily={skin.fontFamily}>
          {label}
        </text>
      </g>
    </g>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/*  SCENE 2: Day / Night                                                       */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * Day/Night spin (~8 s loop):
 *   Earth (large circle with simplified continents) spins at centre.
 *   Left side = day (sun, bright sky half); right side = night (moon, stars).
 *   A house sits at the bottom of the Earth; its window is lit (yellow) on
 *   the day side and dark (grey) on the night side.
 *   The sky cross-fades day (light blue) ↔ night (dark blue) over ~8 s.
 */
export const SkinDayNightScene: React.FC<SkinSceneProps> = ({
  opacity = 1,
  t = 0,
  label = "Earth spins to make day and night!",
}) => {
  const skin = useSkin();

  const CYCLE = 8;
  // dayFrac: 0 = full day, 1 = full night, oscillates 0→1→0 each cycle
  const dayFrac = (Math.sin((t / CYCLE) * Math.PI * 2 - Math.PI / 2) + 1) / 2; // 0→1→0

  /* ── sky gradient simulation: two rects, cross-fade ── */
  const daySkyColor = "#bae6fd";
  const nightSkyColor = "#0f172a";

  /* ── Earth position ── */
  const earthX = 400;
  const earthY = 240;
  const earthR = 110;

  /* ── Earth spin: continents as ellipses that orbit via rotate(t * 8) ── */
  const earthSpin = (t * 8) % 360; // 8 degrees/s

  /* ── house at bottom of earth ── */
  // house sits on land; window brightness = day vs night
  const houseX = 540;
  const houseY = 360;
  const windowColor = dayFrac < 0.5
    ? "#fde047" // bright yellow in day
    : "#374151"; // dark grey at night

  /* ── moon distance from earth on night side ── */
  const moonX = 650;
  const moonY = 140;

  /* ── stars (5) on night side — twinkle ── */
  const starPositions: Array<[number, number]> = [
    [620, 60], [680, 95], [710, 50], [740, 120], [665, 30],
  ];

  /* ── night overlay opacity — fades in as dayFrac increases ── */
  const nightOverlayOpacity = dayFrac * 0.82;

  return (
    <g opacity={opacity}>
      {/* Day sky base */}
      <rect width={800} height={450} fill={daySkyColor} />
      {/* Night overlay — fades in on top */}
      <rect width={800} height={450} fill={nightSkyColor} opacity={nightOverlayOpacity} />

      {/* Horizon ground band */}
      <SkRect x={0} y={370} w={800} h={80} fill="#86efac" seed={3001} opacity={1 - dayFrac * 0.6} />

      {/* Sun (day side, left) — fades out as night comes */}
      <g opacity={Math.max(0, 1 - dayFrac * 2.2)}>
        {skSun(130, 100, t, 1.3)}
      </g>

      {/* Moon + Stars (night side, right) — fade in as night comes */}
      <g opacity={Math.min(1, dayFrac * 2)}>
        {skMoon(moonX, moonY, t, 1.1)}
        {starPositions.map(([sx, sy], i) => (
          <g key={i}>{skStar(sx, sy, t, 0.9)}</g>
        ))}
      </g>

      {/* Earth — large circle */}
      <SkCircle cx={earthX} cy={earthY} r={earthR} fill="#1d4ed8" seed={3010} />

      {/* Continents — ellipses that rotate with the Earth spin */}
      <g transform={`translate(${earthX} ${earthY}) rotate(${earthSpin})`}>
        {/* Americas-ish blob */}
        <SkEllipse cx={-40} cy={-20} rx={26} ry={38} fill="#22c55e" seed={3011} />
        {/* Europe/Africa-ish blob */}
        <SkEllipse cx={30} cy={-10} rx={18} ry={42} fill="#22c55e" seed={3012} />
        {/* Asia-ish blob */}
        <SkEllipse cx={65} cy={-30} rx={30} ry={24} fill="#22c55e" seed={3013} />
        {/* Small island */}
        <SkEllipse cx={-60} cy={40} rx={12} ry={8} fill="#22c55e" seed={3014} />
      </g>

      {/* Earth ice caps */}
      <SkEllipse cx={earthX} cy={earthY - earthR + 14} rx={40} ry={16} fill="#f1f5f9" seed={3015} />
      <SkEllipse cx={earthX} cy={earthY + earthR - 14} rx={34} ry={12} fill="#f1f5f9" seed={3016} />

      {/* Day / Night terminator line — vertical band across Earth */}
      {/* A dark semi-circle sweeps across, driven by dayFrac */}
      <g>
        <defs>
          <clipPath id="earth-clip-3000">
            <circle cx={earthX} cy={earthY} r={earthR} />
          </clipPath>
        </defs>
        {/* Night shadow half — rotates as dayFrac changes */}
        <g clipPath="url(#earth-clip-3000)">
          <SkRect
            x={earthX + Math.cos(Math.PI * dayFrac) * earthR - earthR * 0.1}
            y={earthY - earthR}
            w={earthR * 1.1}
            h={earthR * 2}
            fill="rgba(0,0,30,0.55)"
            seed={3017}
          />
        </g>
      </g>

      {/* House (on land, right of centre) */}
      {skHouse(houseX, houseY, 1.0)}

      {/* Override window colour to show day/night — draw it on top of house */}
      <SkRect x={houseX + 18} y={houseY - 62} w={22} h={22} fill={windowColor} seed={3020} />
      {/* Window glow at night */}
      {dayFrac > 0.4 && (
        <SkEllipse
          cx={houseX + 29}
          cy={houseY - 51}
          rx={20}
          ry={14}
          fill="#fde047"
          opacity={(dayFrac - 0.4) * 0.4}
          seed={3021}
        />
      )}

      {/* Day/Night label lines */}
      <g opacity={Math.max(0, 1 - dayFrac * 2.5)}>
        <text x={140} y={200} textAnchor="middle" fill="#b45309" fontSize={13} fontWeight={700} fontFamily={skin.fontFamily}>
          DAY
        </text>
      </g>
      <g opacity={Math.min(1, dayFrac * 2.5)}>
        <text x={660} y={200} textAnchor="middle" fill="#e2e8f0" fontSize={13} fontWeight={700} fontFamily={skin.fontFamily}>
          NIGHT
        </text>
      </g>

      {/* Caption */}
      <g transform="translate(400 40)">
        <rect x={-265} y={-22} width={530} height={40} rx={20} fill="rgba(255,255,255,0.86)" stroke="#cbd5e1" strokeWidth={1.5} />
        <text x={0} y={4} textAnchor="middle" fill={skin.textInk} fontSize={16} fontWeight={700} fontFamily={skin.fontFamily}>
          {label}
        </text>
      </g>
    </g>
  );
};
